import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';

export default function History() {
  const [filterDay, setFilterDay] = useState('');
  const queryClient = useQueryClient();
  const { data: programs = [] } = useQuery({ queryKey: ['programs'], queryFn: api.getPrograms });
  const colorOf = Object.fromEntries(programs.map((p) => [p.name, p.color]));

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: api.getSessions,
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const handleExportCsv = async () => {
    const token = localStorage.getItem('muscuapp_token');
    const res = await fetch('/api/sessions/export.csv', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `muscuapp-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filterDay ? sessions.filter((s) => s.day === filterDay) : sessions;

  // Group by month
  const grouped = {};
  for (const s of filtered) {
    const key = s.date.slice(0, 7);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  }

  return (
    <div>
      <h1 className="page-title">Historique</h1>
      <p className="page-subtitle">Toutes tes séances enregistrées</p>

      {/* Filter + Export */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className={`btn ${filterDay === '' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setFilterDay('')}
        >
          Tous
        </button>
        {[...new Set([...programs.map((p) => p.name), ...sessions.map((x) => x.day)])].map((g) => (
          <button
            key={g}
            className={`btn ${filterDay === g ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setFilterDay(filterDay === g ? '' : g)}
          >
            {g}
          </button>
        ))}
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={handleExportCsv} title="Exporter en CSV">
          ⬇️ Export CSV
        </button>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" />Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📭</div>
          <p>Aucune séance trouvée</p>
        </div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([month, monthSessions]) => (
            <div key={month} style={{ marginBottom: 32 }}>
              <h3 style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                {new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {monthSessions.map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Link to={`/history/${s.id}`} className="session-item" style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: 4,
                            height: 40,
                            borderRadius: 2,
                            background: colorOf[s.day] || '#10b981',
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div className="session-day-name">{s.day}</div>
                          <div className="session-date">
                            {new Date(s.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, color: 'var(--accent2)' }}>{s.total_sets} séries</div>
                        <div className="session-date">Voir détail →</div>
                      </div>
                    </Link>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--danger)', borderColor: 'transparent' }}
                      onClick={() => {
                        if (window.confirm('Supprimer cette séance ?')) {
                          deleteMutation.mutate(s.id);
                        }
                      }}
                      title="Supprimer"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
      )}
    </div>
  );
}
